import React from "react";
import { GraduationCap, Music, Mic2, Music4, MessageCircle, Flame, HandHeart, Sparkles, ShoppingCart, Clock, ExternalLink } from "lucide-react";
import Reveal from "@/components/Reveal";
import { MINISTRY } from "@/data/site";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { purchaseDetails } from "@/lib/purchases";

const ICONS = [GraduationCap, Music, Mic2, Music4];
const PILLARS = ["Adorer", "Servir", "Élever", "Impacter"];

const enroll = (name) => {
  const msg = encodeURIComponent(
    `Bonjour, je souhaite m'inscrire à la formation « ${name} » (EICE — École Internationale des Chantres de l'Éternel). Merci de me communiquer les détails.`
  );
  const url = `https://wa.me/${MINISTRY.whatsappFr}?text=${msg}`;
  const win = window.open(url, "_blank", "noopener,noreferrer");
  if (!win) {
    try { window.top.location.href = url; } catch { window.location.href = url; }
  }
};

const FormationCard = ({ formation, index }) => {
  const Icon = ICONS[index % ICONS.length];
  return (
    <div
      data-testid={`eice-formation-${formation.id}`}
      className="group relative flex flex-col w-full rounded-2xl overflow-hidden border border-gold/30 bg-gradient-to-b from-[#0b1436] to-[#080b18] p-7 transition-all duration-300 hover:-translate-y-1 hover:border-gold"
      style={{ boxShadow: "0 12px 40px rgba(5,8,20,0.35)" }}
    >
      <div className="flex items-center justify-between mb-5">
        <span className="flex items-center justify-center w-14 h-14 rounded-full bg-gold/15 text-gold-light border border-gold/30">
          <Icon size={26} />
        </span>
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gold text-[#0b1436] text-[11px] font-bold uppercase tracking-wider">
          Formation
        </span>
      </div>
      <h3 className="font-heading text-xl font-medium text-ivory leading-snug">{formation.name}</h3>
      <p className="text-sm text-ivory/60 mt-2 leading-relaxed flex-1">{formation.description}</p>
      {Number(formation.price) > 0 && (
        <div className="flex items-center justify-between mt-6">
          <span className="font-heading text-2xl font-semibold text-gold-light">
            {Number(formation.price).toFixed(2)} €
          </span>
        </div>
      )}
      <button
        onClick={() => enroll(formation.name)}
        data-testid={`eice-enroll-${formation.id}`}
        className="mt-4 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-gold text-[#0b1436] text-sm font-bold hover:bg-gold-light transition-colors duration-300"
      >
        <MessageCircle size={16} /> S'inscrire sur WhatsApp
      </button>
    </div>
  );
};

const AtelierThumb = ({ atelier }) => {
  const { addItem, openCart } = useCart();
  const hasVariants = atelier.variants && atelier.variants.length > 0;
  const purchase = purchaseDetails(atelier);
  const purchasable = purchase.external || (purchase.onsite && (Boolean(atelier.digital_file) || (hasVariants && atelier.variants.some((v) => v.file))));

  const handleAdd = () => {
    addItem(atelier, { qty: 1 });
    toast.success("Ajouté au panier", { description: atelier.name });
    openCart();
  };

  return (
    <div
      data-testid={`eice-atelier-${atelier.id}`}
      className={`group relative w-full sm:w-[520px] text-left rounded-2xl overflow-hidden border border-gold/30 shadow-xl shadow-[#0b1436]/25 transition-all duration-300 ${purchasable ? "hover:-translate-y-1 hover:border-gold" : ""}`}
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={(atelier.images && atelier.images[0]) || atelier.image}
          alt={atelier.name}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        {Number(atelier.price) > 0 && (
          <span
            data-testid={`eice-atelier-price-${atelier.id}`}
            className="absolute top-3 right-3 inline-flex items-center px-3.5 py-1.5 rounded-full bg-gold text-[#0b1436] text-sm font-bold shadow-lg"
          >
            {Number(atelier.price).toFixed(2)} €
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 bg-[#0b1436] px-5 py-4">
        <span data-testid={`eice-atelier-title-${atelier.id}`} className="font-heading text-base md:text-lg text-ivory leading-snug flex-1 min-w-[140px]">{atelier.name}</span>
        {purchase.external ? (
          <a href={purchase.url} target="_blank" rel="noopener noreferrer" data-testid={`eice-atelier-add-${atelier.id}`} className="inline-flex items-center gap-1.5 shrink-0 px-4 py-2 rounded-full bg-gold text-[#0b1436] text-sm font-bold hover:bg-gold-light transition-colors">
            <ExternalLink size={15} /> Acheter
          </a>
        ) : purchasable ? (
          <button
            type="button"
            onClick={handleAdd}
            data-testid={`eice-atelier-add-${atelier.id}`}
            className="inline-flex items-center gap-1.5 shrink-0 px-4 py-2 rounded-full bg-gold text-[#0b1436] text-sm font-bold hover:bg-gold-light transition-colors"
          >
            <ShoppingCart size={15} /> Ajouter au panier
          </button>
        ) : (
          <span data-testid={`eice-atelier-unavailable-${atelier.id}`} className="inline-flex items-center gap-1.5 shrink-0 px-4 py-2 rounded-full bg-white/10 text-ivory/70 text-xs font-semibold border border-gold/20">
            <Clock size={13} /> Bientôt
          </span>
        )}
      </div>
    </div>
  );
};

export const EiceSection = ({ formations, ateliers }) => {
  const courses = (formations || []).filter((p) => p.format !== "Atelier");
  const num = (n) => parseInt((String(n).match(/N°\s*(\d+)/) || [])[1] || 0, 10);
  const atelierList = (ateliers || []).slice().sort((a, b) => num(a.name) - num(b.name));
  return (
    <div data-testid="eice-section">
      {/* Banner */}
      <Reveal>
        <div className="relative rounded-3xl overflow-hidden border border-gold/30 shadow-2xl shadow-[#0b1436]/30">
          <img
            src="/eice-banner.jpg"
            alt="EICE — École Internationale des Chantres de l'Éternel — Prophète Steeve Orphôss"
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </Reveal>

      {/* Intro */}
      <Reveal delay={0.1}>
        <div className="max-w-3xl mx-auto text-center mt-12">
          <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold font-semibold mb-4">
            <Sparkles size={14} /> Formation · Adoration · Service
          </p>
          <h2 className="font-heading text-3xl md:text-4xl font-light text-brown-black tracking-tight">
            École Internationale des Chantres de l'Éternel
          </h2>
          <div className="gold-line w-24 mx-auto my-7" />
          <p className="text-brown-black/75 leading-relaxed">
            À travers cette série d'enseignements audio, l'École Internationale des Chantres de l'Éternel
            (EICE) forme et équipe les chantres, les adorateurs et les conducteurs de louange afin qu'ils
            servent Dieu avec <span className="text-brown-dark font-semibold">excellence, consécration et
            onction</span>. Grâce à des enseignements bibliques solides et à un accompagnement pratique,
            l'EICE vous aide à développer votre appel, à mieux adorer, à servir efficacement, à élever le
            nom de Dieu et à impacter les nations par le chant et la louange.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            {PILLARS.map((p) => (
              <span key={p} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gold/25 text-sm font-semibold text-brown-dark">
                {p === "Adorer" && <Flame size={15} className="text-gold" />}
                {p === "Servir" && <HandHeart size={15} className="text-gold" />}
                {p === "Élever" && <Music size={15} className="text-gold" />}
                {p === "Impacter" && <GraduationCap size={15} className="text-gold" />}
                {p}
              </span>
            ))}
          </div>
        </div>
      </Reveal>

      {/* Formations */}
      <div className="mt-14">
        <h3 className="font-heading text-2xl md:text-3xl font-medium text-brown-black text-center mb-10">
          Nos formations
        </h3>
        {courses.length === 0 ? (
          <p className="text-center text-brown-black/50 py-6" data-testid="eice-empty">
            Les formations seront bientôt disponibles.
          </p>
        ) : (
          <div className="flex flex-wrap justify-center gap-8">
            {courses.map((f, i) => (
              <Reveal key={f.id} delay={(i % 3) * 0.1} className="w-full sm:w-[350px] flex">
                <FormationCard formation={f} index={i} />
              </Reveal>
            ))}
          </div>
        )}
      </div>

      {/* Ateliers */}
      {atelierList.length > 0 && (
        <div className="mt-16">
          <h3 className="font-heading text-2xl md:text-3xl font-medium text-brown-black text-center mb-10">
            Ateliers audio
          </h3>
          <div className="flex flex-wrap justify-center gap-8">
            {atelierList.map((a, i) => (
              <Reveal key={a.id} delay={(i % 2) * 0.1} className="w-full sm:w-[520px] flex">
                <AtelierThumb atelier={a} />
              </Reveal>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EiceSection;
